import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  LinearProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardHeader,
  Button,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  ExpandLess as ExpandLessIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  SmartToy as SmartToyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Psychology as PsychologyIcon,
  PictureAsPdf
} from '@mui/icons-material';
import { obterSimilaridadeProjeto } from '../../services/projetoService';
import { useRelatorioExport } from '../../services/relatorioExportService';

const RelatorioSimilaridadeProjeto = ({ projetoId, projeto }) => {
  // ===== ESTADOS SEGUROS (MESMO PADRÃO DAS MONOGRAFIAS) =====
  const [similares, setSimilares] = useState([]);
  const [antiPlagio, setAntiPlagio] = useState({
    sistemaHibrido: false,
    totais: { total: 0, monografias: 0, arquivos_digitais: 0, projetos: 0, fontes_externas: 0 },
    threshold: 0.3
  });
  const [iaDetection, setIaDetection] = useState({
    disponivel: false,
    score: 0,
    classificacao: 'Sem dados',
    recomendacao: 'Sistema não disponível'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandido, setExpandido] = useState(false);
  const [analisado, setAnalisado] = useState(false);

  // Hook para exportação PDF
  const { exportar, exportando } = useRelatorioExport();

  const iniciarAnalise = async () => {
    if (!projetoId) return;
    try {
      setLoading(true);
      setError(null);
      setAnalisado(false);

      const response = await obterSimilaridadeProjeto(projetoId);

      const similaresData = response?.similares || [];
      const antiPlagioData = response?.antiPlagio || {
        sistemaHibrido: false,
        totais: { total: 0, monografias: 0, arquivos_digitais: 0, projetos: 0, fontes_externas: 0 },
        threshold: 0.3
      };
      const iaDetectionData = response?.iaDetection || {
        disponivel: false,
        score: 0,
        classificacao: 'Sem dados',
        recomendacao: 'Sistema não disponível'
      };

      setSimilares(similaresData);
      setAntiPlagio(antiPlagioData);
      setIaDetection(iaDetectionData);
      setAnalisado(true);
      setExpandido(true);
    } catch (err) {
      console.error('Erro ao buscar similaridades do projeto:', err);
      setError('Não foi possível carregar o relatório de integridade do projeto.');
      setAnalisado(true);
      setExpandido(true);
    } finally {
      setLoading(false);
    }
  };

  // ===== FUNÇÃO PARA EXPORTAR PDF DO PROJETO =====
  const handleExportarPDF = async () => {
    try {
      const dadosProjeto = {
        id: projetoId,
        titulo: projeto?.proposta?.titulo || projeto?.titulo || `Projeto ${projetoId}`,
        estudante: projeto?.proposta?.estudante?.nome || projeto?.estudante || 'Estudante não identificado',
        supervisor: projeto?.supervisor?.Usuario?.nome || projeto?.supervisor || 'Supervisor não identificado',
        dataSubmissao: projeto?.created_at || projeto?.dataSubmissao || new Date().toISOString(),
        versao: projeto?.versao || '1.0',
        status: projeto?.status || 'Em análise'
      };

      const dadosSimilaridade = {
        similares,
        antiPlagio,
        iaDetection
      };

      console.log('📊 Exportando relatório do projeto:', dadosProjeto);
      await exportar(dadosProjeto, dadosSimilaridade);
      
    } catch (error) {
      console.error('❌ Erro ao exportar PDF do projeto:', error);
    }
  };

  // ===== FUNÇÕES AUXILIARES =====
  const toggleExpandir = () => {
    setExpandido(!expandido);
  };

  const getIAStatusColor = (score) => {
    if (!score) return 'success';
    if (score >= 80) return 'error';
    if (score >= 50) return 'warning';
    return 'success';
  };

  const getIAStatusIcon = (score) => {
    if (!score) return <CheckCircleIcon fontSize="small" />;
    if (score >= 80) return <ErrorIcon fontSize="small" />;
    if (score >= 50) return <WarningIcon fontSize="small" />;
    return <CheckCircleIcon fontSize="small" />;
  };

  const safeGet = (obj, path, defaultValue = 0) => {
    try {
      const keys = path.split('.');
      let result = obj;
      for (const key of keys) {
        result = result?.[key];
      }
      return result !== undefined ? result : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  if (!analisado && !loading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Relatório de Integridade do Projeto
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clique no botão para iniciar a verificação de plágio e detecção de conteúdo gerado por IA.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SecurityIcon />}
            onClick={iniciarAnalise}
          >
            Analisar Integridade
          </Button>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body1">A analisar integridade do projeto... Isso pode levar alguns instantes.</Typography>
        </Paper>
      </Box>
    );
  }


  const hasHighSimilarity = similares.length > 0 && similares[0]?.score >= 60;
  const hasHighIA = iaDetection?.disponivel && iaDetection?.score >= 70;
  const hasAlerts = hasHighSimilarity || hasHighIA;

  return (
    <Card sx={{ mt: 3, mb: 3 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" component="div">
                Relatório de Integridade do Projeto
              </Typography>
              {hasAlerts && (
                <Chip 
                  icon={<WarningIcon />} 
                  label="Requer Atenção" 
                  color="warning" 
                  size="small" 
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Executar nova análise (consome quota)">
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={iniciarAnalise}
                  disabled={loading}
                  sx={{ textTransform: 'none', minWidth: 120 }}
                >
                  Re-analisar
                </Button>
              </Tooltip>

              <Tooltip title="Exportar relatório completo do projeto em PDF">
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={exportando ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
                  onClick={handleExportarPDF}
                  disabled={exportando}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 'bold',
                    minWidth: 140,
                    boxShadow: 1,
                    '&:hover': {
                      boxShadow: 2,
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  {exportando ? 'Gerando...' : 'Exportar PDF'}
                </Button>
              </Tooltip>
              
              <IconButton onClick={toggleExpandir}>
                {expandido ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>
        }
      />
      
      <Collapse in={expandido}>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} action={
              <Button color="inherit" size="small" onClick={iniciarAnalise}>Tentar novamente</Button>
            }>
              {error}
            </Alert>
          )}

          {/* Indicador de progresso da exportação PDF */}
          {exportando && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                <Typography variant="body2">
                  Gerando relatório PDF do projeto... Por favor, aguarde.
                </Typography>
              </Box>
            </Alert>
          )}
          
          {/* === RESUMO EXECUTIVO === */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PsychologyIcon sx={{ mr: 1 }} />
              Resumo Executivo
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ 
                  bgcolor: hasHighSimilarity ? 'error.light' : 'success.light',
                  color: hasHighSimilarity ? 'error.contrastText' : 'success.contrastText'
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4">
                      {similares.length > 0 ? (similares[0]?.score?.toFixed(1) || '0') : '0'}%
                    </Typography>
                    <Typography variant="body2">Similaridade Máxima</Typography>
                    <Chip 
                      label={hasHighSimilarity ? 'Alto Risco' : 'Normal'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ 
                  bgcolor: iaDetection?.disponivel ? 
                    (iaDetection?.score >= 70 ? 'error.light' : iaDetection?.score >= 50 ? 'warning.light' : 'success.light') : 
                    'grey.100'
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4">
                      {iaDetection?.disponivel ? (iaDetection?.score || '0') : '—'}%
                    </Typography>
                    <Typography variant="body2">Detecção de IA</Typography>
                    <Chip 
                      label={iaDetection?.disponivel ? (iaDetection?.classificacao || 'N/A') : 'Sem dados'}
                      size="small"
                      color={iaDetection?.disponivel ? 'default' : 'info'}
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ bgcolor: 'info.light' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h4">
                      {safeGet(antiPlagio, 'totais.total', 0)}
                    </Typography>
                    <Typography variant="body2">Projetos Verificados</Typography>
                    <Chip 
                      label={antiPlagio?.sistemaHibrido ? 'Sistema Híbrido' : 'Sistema Básico'}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* === DETECÇÃO DE IA === */}
          {iaDetection?.disponivel && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <SmartToyIcon sx={{ mr: 1 }} />
                Verificação de Conteúdo Gerado por IA
              </Typography>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {getIAStatusIcon(iaDetection.score)}
                        <Typography variant="h5" sx={{ ml: 1, mr: 2 }}>
                          {iaDetection.score || '0'}%
                        </Typography>
                        <Chip 
                          label={iaDetection.classificacao || 'N/A'}
                          color={getIAStatusColor(iaDetection.score)}
                          variant="outlined"
                        />
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={iaDetection.score || 0} 
                        color={getIAStatusColor(iaDetection.score)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Recomendação:
                      </Typography>
                      <Alert 
                        severity={getIAStatusColor(iaDetection.score)}
                        sx={{ mt: 1 }}
                      >
                        {iaDetection.recomendacao || 'Nenhuma recomendação disponível.'}
                      </Alert>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* === VERIFICAÇÃO ANTI-PLÁGIO === */}
          {antiPlagio?.totais && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <SecurityIcon sx={{ mr: 1 }} />
                Sistema Anti-Plágio
              </Typography>
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Configuração do Sistema:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Chip 
                          icon={antiPlagio.sistemaHibrido ? <CheckCircleIcon /> : <ErrorIcon />}
                          label={`Sistema ${antiPlagio.sistemaHibrido ? 'Híbrido' : 'Básico'}`}
                          color={antiPlagio.sistemaHibrido ? 'success' : 'warning'}
                          variant="outlined"
                        />
                        <Typography variant="body2">
                          <strong>Limite de Detecção:</strong> {(antiPlagio.threshold || 0.3) * 100}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        Fontes Verificadas:
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'primary.light' }}>
                            <Typography variant="h6">{antiPlagio.totais?.projetos ?? 0}</Typography>
                            <Typography variant="caption">Projectos</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={4}>
                          <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'secondary.light' }}>
                            <Typography variant="h6">{antiPlagio.totais?.arquivos_digitais ?? 0}</Typography>
                            <Typography variant="caption">Arquivos</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={4}>
                          <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'info.light' }}>
                            <Typography variant="h6">{antiPlagio.totais?.fontes_externas ?? 0}</Typography>
                            <Typography variant="caption">Externas</Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* === ANÁLISE DE SIMILARIDADE === */}
          {similares.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <WorkIcon sx={{ mr: 1 }} />
                Análise Detalhada de Similaridade
              </Typography>

              {/* Cards de métricas detalhadas */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (similares[0]?.similaridadeDireta >= 0.6) ? 'error.lighter' : 'background.default',
                    }}
                  >
                    <Typography variant="h5" color={(similares[0]?.similaridadeDireta >= 0.6) ? 'error' : 'inherit'}>
                      {similares[0]?.similaridadeDireta ? (similares[0].similaridadeDireta * 100).toFixed(1) : '—'}%
                    </Typography>
                    <Typography variant="caption">Similaridade Direta</Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (similares[0]?.jaroWinkler >= 0.6) ? 'error.lighter' : 'background.default',
                    }}
                  >
                    <Typography variant="h5" color={(similares[0]?.jaroWinkler >= 0.6) ? 'error' : 'inherit'}>
                      {similares[0]?.jaroWinkler ? (similares[0].jaroWinkler * 100).toFixed(1) : '—'}%
                    </Typography>
                    <Typography variant="caption">Similaridade Jaro-Winkler</Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      bgcolor: (similares[0]?.score >= 60) ? 'error.lighter' : 'background.default',
                    }}
                  >
                    <Typography variant="h5" color={(similares[0]?.score >= 60) ? 'error' : 'inherit'}>
                      {similares[0]?.score?.toFixed(1) || '0'}%
                    </Typography>
                    <Typography variant="caption">Score Combinado</Typography>
                  </Paper>
                </Grid>
              </Grid>
              
              {/* Alerta de alta similaridade */}
              {similares[0]?.score >= 60 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <AlertTitle>⚠️ Alta Similaridade Detectada</AlertTitle>
                  <Typography variant="body2">
                    {`Este projeto apresenta ${(similares[0]?.score || 0).toFixed(1)}% de similaridade com outro projeto existente.`}
                    {similares[0]?.termosComuns?.length > 0 && 
                      ` Os termos em comum são: ${similares[0].termosComuns.join(', ')}.`
                    }
                    <br/><br/>
                    <strong>Recomenda-se uma análise detalhada para verificação de possível duplicação de conteúdo.</strong>
                  </Typography>
                </Alert>
              )}
              
              {/* Termos comuns */}
              {similares[0]?.termosComuns?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Termos em Comum Identificados:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {similares[0].termosComuns.map((termo, index) => (
                      <Chip 
                        key={index} 
                        label={termo} 
                        size="small" 
                        color="warning"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Trechos similares melhorados */}
              {similares[0]?.trechosSimilares?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Trechos com Similaridade Detectada:
                  </Typography>
                  {similares[0].trechosSimilares.map((trecho, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                      <CardHeader
                        title={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2">
                              Trecho Comparativo #{index + 1}
                            </Typography>
                            <Chip 
                              label={`${(trecho.similaridade * 100).toFixed(1)}% similar`}
                              color={trecho.similaridade >= 0.8 ? 'error' : trecho.similaridade >= 0.6 ? 'warning' : 'info'}
                              size="small"
                            />
                          </Box>
                        }
                      />
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom color="primary">
                              📄 Projecto Actual:
                            </Typography>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 2, 
                                bgcolor: 'primary.light',
                                borderLeft: 4,
                                borderLeftColor: 'primary.main'
                              }}
                            >
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                &ldquo;{trecho.trecho1}&rdquo;
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom color="error">
                              📋 Projecto Similar:
                            </Typography>
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 2, 
                                bgcolor: 'error.light',
                                borderLeft: 4,
                                borderLeftColor: 'error.main'
                              }}
                            >
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                &ldquo;{trecho.trecho2}&rdquo;
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          )}
          
          {/* === PROJETOS SIMILARES === */}
          {similares.length > 0 && (
            <Box>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <StorageIcon sx={{ mr: 1 }} />
                Projectos Similares Encontrados
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Título</strong></TableCell>
                      <TableCell><strong>Problema de Pesquisa</strong></TableCell>
                      <TableCell><strong>Estudante</strong></TableCell>
                      <TableCell><strong>Data de Submissão</strong></TableCell>
                      <TableCell align="right"><strong>Similaridade</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {similares.map((similar, index) => (
                      <TableRow 
                        key={similar.id || index}
                        sx={{ 
                          bgcolor: index % 2 === 0 ? 'transparent' : 'grey.25',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {similar.documentoOriginal?.titulo || 'Sem título'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {similar.documentoOriginal?.problemaPesquisa 
                              ? similar.documentoOriginal.problemaPesquisa.substring(0, 100) + '...' 
                              : 'Sem descrição'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            👤 {similar.documentoOriginal?.estudante || 'Não identificado'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {similar.documentoOriginal?.dataSubmissao
                              ? new Date(similar.documentoOriginal.dataSubmissao).toLocaleDateString('pt-BR')
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${(similar.score || 0).toFixed(1)}%`}
                            color={similar.score >= 80 ? "error" : similar.score >= 60 ? "warning" : "info"}
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          
          {/* Aviso sobre responsabilidade - específico para projetos */}
          <Alert severity="info" sx={{ mt: 3 }}>
            <AlertTitle>ℹ️ Informações Importantes sobre Projectos de Pesquisa</AlertTitle>
            <Typography variant="body2">
              Este relatório de integridade é gerado automaticamente para auxiliar na análise de projetos de pesquisa. 
              A presença de similaridade pode indicar uso de metodologias comuns, referências adequadas, terminologia padrão da área 
              ou adaptação de projetos similares aprovados. Para projetos de pesquisa, é normal encontrar similaridades em aspectos 
              metodológicos e fundamentação teórica. Sempre consulte as normas institucionais para interpretação adequada dos resultados.
            </Typography>
          </Alert>
        </CardContent>
      </Collapse>
    </Card>
  );
};

RelatorioSimilaridadeProjeto.propTypes = {
  projetoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  projeto: PropTypes.object,
};

export default RelatorioSimilaridadeProjeto;